import React, { useState } from 'react'
import Text from 'antd/es/typography/Text'
import SortableList from '../sortable-list/SortableList'
import { DragHandle, SortableItem } from '../sortable-list/SortableItem'
import { Button, Form, Space } from 'antd'
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    EditOutlined,
    LoadingOutlined,
    MinusCircleOutlined,
    PlusOutlined,
    ReloadOutlined,
} from '@ant-design/icons'
import _, { isNumber } from 'lodash'
import { v4 } from 'uuid'
import TextArea from 'antd/es/input/TextArea'
import ReferenceReasonings from '../../../pages/assignment/components/file-drawer/file-criteria-reasoning/ReferenceReasonings'

const CriteriaFields = ({ 
    fields, 
    setFields, 
    regenerateReasoning, 
    setFocusedCriterion, 
    token, 
    layerId, 
    patternId, 
    onOpenFile,
    getReferenceReasoningsForCriterion
}) => {
    const [isWaitingForProjectCriteria, setIsWaitingForProjectCriteria] = useState(false)

    const handleCriterionChange = (value, id) => {
        const _criteriaFields = _.cloneDeep(fields)
        const criterionToUpdate = _criteriaFields.find(criterion => criterion.id === id)
        criterionToUpdate.value = value
        setFields(_criteriaFields)
    }

    const removeCriterionField = id => {
        setFields(fields.filter(criterion => criterion.id !== id))
    }

    const addCriterionField = () => {
        setFields([...fields, { id: v4(), value: '' }])
    }

    const handleRegenerateReasoning = async () => {
        setIsWaitingForProjectCriteria(true)
        const newProjection = await regenerateReasoning(fields.map(field => field.value))
        if (newProjection)
            setFields(
                newProjection.map(projection => ({
                    id: v4(),
                    value: projection.criterion?.trim(),
                    isMet: projection.is_met,
                    reasoning: projection.reasoning,
                    matchConfidenceInPercent: isNumber(projection.match_confidence) ? projection.match_confidence * 100 : null,
                })),
            )
        setIsWaitingForProjectCriteria(false)
    }

    const handleFocusCriterionInput = criterion => {
        if (setFocusedCriterion) setFocusedCriterion(criterion)
    }

    const handleBlurCriterionInput = () => {
        if (setFocusedCriterion) setFocusedCriterion(null)
    }

    return (
        <>
            <Space size={8}>
                <Text className='mb-8'>Criteria</Text>
                {regenerateReasoning && (
                    <Button
                        type='text'
                        shape='circle'
                        title='Regenerate reasoning'
                        loading={isWaitingForProjectCriteria}
                        icon={<ReloadOutlined />}
                        onClick={handleRegenerateReasoning}
                    />
                )}
            </Space>
            <SortableList
                items={fields}
                onChange={setFields}
                header={null}
                onChangeWithCustomMovingLogic={null}
                renderItem={(field, index) => {
                    let criterionPrefix
                    let reasoning

                    if (field.reasoning) {
                        if (isWaitingForProjectCriteria) {
                            criterionPrefix = <LoadingOutlined style={{ color: 'blue' }} spin />
                        } else if (field.isMet === true) {
                            criterionPrefix = (
                                <CheckCircleOutlined title='Criterion is met' style={{ color: 'green' }} />
                            )
                        } else if (field.isMet === false) {
                            criterionPrefix = (
                                <CloseCircleOutlined title='Criterion is not met' style={{ color: 'red' }} />
                            )
                        }

                        if (isWaitingForProjectCriteria) {
                            reasoning = 'Regenerating reasoning...'
                        } else if (field.reasoning) {
                            const matchConfidence = isNumber(field.matchConfidenceInPercent)
                                ? ` (Match confidence: ${field.matchConfidenceInPercent}%)`
                                : ''
                            reasoning = `${field.reasoning}${matchConfidence}`
                        }
                    }

                    // Get reference reasonings for this criterion
                    const references = getReferenceReasoningsForCriterion?.(field.id, index)

                    return (
                        <SortableItem id={field.id} index={index} key={field.id}>
                            <div className='mb-16'>
                                <Space.Compact block>
                                    <DragHandle tooltipText='Move criterion' />
                                    <TextArea
                                        placeholder='Criterion'
                                        aria-label={`Criterion ${index + 1}`}
                                        autoSize={{ minRows: 1, maxRows: 6 }}
                                        value={field.value}
                                        onChange={event => handleCriterionChange(event.target.value, field.id)}
                                        onFocus={() => handleFocusCriterionInput(field.value)}
                                        onBlur={handleBlurCriterionInput}
                                    />
                                    {fields.length > 1 ? (
                                        <Button
                                            icon={<MinusCircleOutlined />}
                                            title='Remove criterion'
                                            onClick={() => removeCriterionField(field.id)}
                                        />
                                    ) : null}
                                </Space.Compact>
                                {reasoning && (
                                    <div style={{ display: 'flex', flexWrap: 'nowrap' }}>
                                        <div className='d-flex-center' style={{ minWidth: 32 }}>
                                            {criterionPrefix}
                                        </div>
                                        <div>
                                            {field.hasOverrule && <EditOutlined title='Overruled' style={{ marginRight: '5px' }} />}
                                            <Text type='secondary'>{reasoning}</Text>
                                        </div>
                                    </div>
                                )}
                                {references && (
                                    <ReferenceReasonings
                                        references={references}
                                        token={token}
                                        layerId={layerId}
                                        patternId={patternId}
                                        criterion={{
                                            id: field.id,
                                            criterion: field.value,
                                            reasoning: field.reasoning,
                                            met: field.isMet,
                                            key: index
                                        }}
                                        onOpenFile={onOpenFile}
                                    />
                                )}
                            </div>
                        </SortableItem>
                    )
                }}
            />
            <Form.Item>
                <Button type='dashed' onClick={addCriterionField} icon={<PlusOutlined />} block>
                    Add criterion
                </Button>
            </Form.Item>
        </>
    )
}

export default CriteriaFields
